import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart2,
  Receipt,
  CreditCard,
  Wallet,
  Users2,
  Shield,
  MessagesSquare,
  Video,
  Settings,
  HelpCircle,
  LucideIcon,
  Cpu,
  MapPin,
  Cuboid,
  Home,
  Brain,
  X,
} from 'lucide-react';
import { useSidebar } from '@/contexts/sidebar-context';

export default function UserSidebar() {
  const { isOpen, isMobile, close } = useSidebar();
  const router = useRouterState();

  function handleNavigation() {
    // Close sidebar on mobile after navigation
    if (isMobile) {
      close();
    }
  }

  function NavItem({ href, icon: Icon, children }: { href: string; icon: LucideIcon; children: React.ReactNode }) {
    // Determine if this nav item is active
    const isActive = router.location.pathname === href || (href !== '#' && router.location.pathname.startsWith(href));
    return (
      <Link
        to={href}
        onClick={handleNavigation}
        className={`flex items-center px-3 py-2 text-sm rounded-md font-medium transition-colors
          ${isActive ? 'text-primary bg-primary/10 dark:bg-primary/20' : 'text-gray-700 dark:text-gray-200'}
          hover:text-primary dark:hover:text-primary hover:bg-gray-100 dark:hover:bg-[#23232a]`}
        aria-current={isActive ? 'page' : undefined}
      >
        <Icon className={`h-4 w-4 mr-3 flex-shrink-0 opacity-80 ${isActive ? 'text-primary' : ''}`} />
        {children}
        {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary shadow-sm" />}
      </Link>
    );
  }

  return (
    <>
      <nav
        className={`
          fixed inset-y-0 left-0 z-[70] w-64 transform transition-transform duration-200 ease-in-out
          border-r bg-gradient-to-b from-white via-gray-50 to-gray-100 dark:from-[#101014] dark:via-[#18181c] dark:to-[#23232a]
          border-gray-200 dark:border-[#23232a]
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="h-full flex flex-col">
          <div className="h-16 px-6 flex items-center justify-between border-b border-gray-200 dark:border-[#23232a] bg-white dark:bg-[#101014]">
            <a
              href="https://webiotpro.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3"
            >
              <img
                src="https://kokonutui.com/logo.svg"
                alt="Web IoT Pro"
                width={32}
                height={32}
                className="flex-shrink-0 hidden dark:block"
              />
              <img
                src="https://kokonutui.com/logo-black.svg"
                alt="Web IoT Pro"
                width={32}
                height={32}
                className="flex-shrink-0 block dark:hidden"
              />
              <span className="text-lg font-semibold hover:cursor-pointer text-gray-900 dark:text-white">
                Web IoT Pro ✨
              </span>
            </a>
            {/* Close button for mobile */}
            {isMobile && (
              <button
                type="button"
                onClick={close}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#23232a] transition-colors"
                aria-label="Close sidebar"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto py-4 px-4">
            <div className="space-y-6">
              <SidebarSection title="Overview">
                <NavItem href="#" icon={Home}>
                  Dashboard
                </NavItem>
              </SidebarSection>

              <SidebarSection title="AI & Analytics">
                <NavItem href="/dashboard/analytics" icon={BarChart2}>
                  Analytics
                </NavItem>
                {/* <NavItem href="/dashboard/scenes" icon={Cuboid}>
                  3D Scenes
                </NavItem> */}
                <NavItem href="/dashboard/ai-insights" icon={Brain}>
                  AI Insights
                </NavItem>
              </SidebarSection>

              <SidebarSection title="Configure">
                {/* <NavItem href="/dashboard/widgets" icon={Receipt}>
                  Widgets
                </NavItem> */}
                <NavItem href="/dashboard/locations" icon={MapPin}>
                  Locations
                </NavItem>
                <NavItem href="/dashboard/devices" icon={Cpu}>
                  Devices
                </NavItem>
              </SidebarSection>

              <SidebarSection title="Team">
                <NavItem href="/dashboard/tenants" icon={Users2}>
                  Members
                </NavItem>
                {/* <NavItem href="#" icon={Shield}>
                  Permissions
                </NavItem>
                <NavItem href="#" icon={MessagesSquare}>
                  Chat
                </NavItem>
                <NavItem href="#" icon={Video}>
                  Surveillance
                </NavItem> */}
              </SidebarSection>

              {/* <SidebarSection title="Subscription">
                <NavItem href="#" icon={Wallet}>
                  Billing
                </NavItem>
                <NavItem href="#" icon={CreditCard}>
                  Payment Methods
                </NavItem>
              </SidebarSection> */}
            </div>
          </div>

          <div className="px-4 py-4 border-t border-gray-200 dark:border-[#23232a] bg-white dark:bg-[#101014]">
            <div className="space-y-1">
              <NavItem href="/dashboard/settings" icon={Settings}>
                Settings
              </NavItem>
              <NavItem href="#" icon={HelpCircle}>
                Help
              </NavItem>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {isOpen && isMobile && (
        <button
          type="button"
          className="fixed inset-0 bg-black/40 z-[65]"
          onClick={close}
          aria-label="Close sidebar overlay"
        />
      )}
    </>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
